import React, {FC} from 'react';

import {CycleMini, Icon} from '@optimaxdev/icons';
import classNames from 'classnames';
import {addDays, format} from 'date-fns';

import {QuoteItemV2} from 'api/magento/magentoAPI.schemas';
import {BaseLensesVistaProductItem} from 'components/baseProductItems/baseLensesVistaProductItem/baseLensesVistaProductItem';
import {RemoveItemButton} from 'components/baseProductItems/removeItemButton/removeItemButton';
import {SupplyText} from 'components/miniCart/supplyText/supplyText';
import {NotificationBlock} from 'components/notificationBlock/notificationBlock';
import {Price} from 'components/price/price';
import {VISTA_LENSES_PER_BOX} from 'constants/product';

import s from '../miniCartProductItem.scss';

export type PropsType = {
    item: QuoteItemV2;
};

/**
 * Adds 10 days to the current date
 * @returns {string} - next shipment date
 */
const getNextShipmentDate = (): string => format(addDays(Date.now(), 10), 'MMM. d');

/**
 * Returns the plan price
 * @param {number} countOfMonths - count of months
 * @param {string} relatedProductSkuPrice - price
 * @returns {React.ReactElement} - price for the subscription plan
 */
const getPlanPrice = (
    countOfMonths: number,
    relatedProductSkuPrice: string,
): React.ReactElement => <Price>{Number(relatedProductSkuPrice) * countOfMonths * 2}</Price>;

const VistaNameComponent = ({item}: {item: QuoteItemV2}) => (
    <p className={s.vistaName}>{item.name}</p>
);

const SupplyComponent = ({item}: {item: QuoteItemV2}) => {
    const countOfSubscriptionMonths = Number(
        item.subscription?.currentSubscriptionPlan?.slice(0, -1),
    );
    return (
        item.subscription && (
            <div className={s.infoBlock}>
                <p className={s.autoRefill}>
                    <Icon
                        className={s.cycleIcon}
                        height={14}
                        icon={CycleMini}
                        strokeWidth={2}
                        width={14}
                    />
                    Auto Refill
                </p>
                <p className={s.simpleRow}>
                    Refill price:{' '}
                    {getPlanPrice(
                        countOfSubscriptionMonths,
                        item.subscription.relatedProductSkuPrice,
                    )}
                </p>
                <p className={s.simpleRow}>Next payment: {getNextShipmentDate()}</p>
                <p className={classNames(s.simpleRow, s.vistaQuantity)}>
                    Qty: {countOfSubscriptionMonths * VISTA_LENSES_PER_BOX * 2} lenses
                </p>
                <SupplyText item={item} inVista longMonth />
            </div>
        )
    );
};

/**
 * Product Contact lenses Vista Plus Item for checkout and cart pages
 */
export const MiniCartLensesVistaItem: FC<PropsType> = ({item}) => {
    const productHasNotification =
        item.product.stock?.isOutOfStock || item.product.stock?.isLowStock;

    return (
        <div className={s.cartItem}>
            <BaseLensesVistaProductItem
                removeComponent={
                    __BUILD_TYPE__ === 'desktop' && <RemoveItemButton item={item} isMiniCart />
                }
                item={item}
                nameComponent={<VistaNameComponent item={item} />}
                supplyComponent={<SupplyComponent item={item} />}
            />
            <NotificationBlock item={item} productHasNotification={productHasNotification} />
        </div>
    );
};
